import isEmpty from "lodash/isEmpty";
import round from "lodash/round";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { formatCurrency } from "../../../../util/formatHelpers";
import PopoverLink from "../../../General/PopoverLink";
import { COMMUNITY_AGENCY_ATTRIBUTES } from "../../JobCasts/create/formSections/Agencies/communityConstants";

import { useStyles, RFI_OPTIMIZED_FEE_OPTION } from "./constants";
import FeeSection, { renderOptimizedRatesIcon } from "./FeeSection";

function AgencyFeesPopover({
  preferredLink,
  standardizedFeeOptions,
  isGreyedOut = false,
}) {
  let title = null;
  const classes = useStyles();
  const tempCommunityTerms = useSelector(
    (state) => state.communityTerms.items?.temp
  );

  const communityConversionFeeOptions = useMemo(
    () =>
      tempCommunityTerms &&
      tempCommunityTerms[Object.keys(tempCommunityTerms)[0]]?.attributes
        ?.conversionFeeOptions,
    [tempCommunityTerms]
  );

  const [permFeeOptions, tempFeeOptions, tempMarkupFeeOptions] = useMemo(() => {
    const {
      permEnabled,
      tempEnabled,
      feeOptions,
      optimizedFee,
      tempFeeOptions,
      tempOptimizedFee,
      tempMarkupEnabled,
      tempMarkupFeeOptions,
    } =
      preferredLink?.attributes ||
      COMMUNITY_AGENCY_ATTRIBUTES.preferredLink.attributes;

    let permOptions;
    let tempOptions;
    let tempMarkupOptions;

    if (permEnabled) {
      if (preferredLink.id === "RecruitiFi Community") {
        permOptions = feeOptions;
      } else if (!optimizedFee && !isEmpty(standardizedFeeOptions)) {
        permOptions = standardizedFeeOptions;
      } else {
        permOptions = optimizedFee ? [RFI_OPTIMIZED_FEE_OPTION] : feeOptions;
      }
    }

    if (tempEnabled) {
      tempOptions = tempOptimizedFee
        ? communityConversionFeeOptions
        : tempFeeOptions;
    }

    if (tempMarkupEnabled) {
      tempMarkupOptions = tempMarkupFeeOptions;
    }

    return [permOptions, tempOptions, tempMarkupOptions];
  }, [standardizedFeeOptions, preferredLink, communityConversionFeeOptions]);

  if (!tempFeeOptions && permFeeOptions[0] === RFI_OPTIMIZED_FEE_OPTION && !tempMarkupFeeOptions) {
    return renderOptimizedRatesIcon();
  }

  if (tempFeeOptions && permFeeOptions) {
    title = "See Details";
  } else if (tempFeeOptions) {
    title = "Role Dependent";
  } else if (!tempFeeOptions && permFeeOptions.length === 1) {
    if (permFeeOptions[0].feeType === "percentage") {
      const percentageValue = round(permFeeOptions[0].feePercentage * 100, 2);

      return `${percentageValue}%`;
    }

    const formattedCurrency = formatCurrency(
      permFeeOptions[0].feeCents,
      permFeeOptions[0].currency,
      { precision: 2 }
    );

    return formattedCurrency.replace(/\.?0+$/, "");
  }

  return (
    <div>
      <PopoverLink
        renderLink={(handleClick) => (
          <span
            className={classes.linkText}
            onClick={handleClick}
            data-cy="fee-option-tooltip"
            style={{ color: isGreyedOut ? "#90A4AE" : "" }}
            role="button"
          >
            {title || `${permFeeOptions?.length} Fees`}
          </span>
        )}
        renderPopoverContent={() => (
          <div data-cy="fee-tooltip-content" className={classes.cardContainer}>
            {permFeeOptions?.length > 0 && (
              <FeeSection
                title="Permanent Placement"
                feeData={permFeeOptions}
              />
            )}
            {(preferredLink?.attributes?.tempEnabled && tempFeeOptions) && (
              <>
                <FeeSection
                  title="Contract Bill Rates"
                  feeData={["Bill rates are specific to each role"]}
                />
                <FeeSection
                  title="Contract Conversion"
                  feeData={tempFeeOptions}
                />
              </>
            )}
            {(preferredLink?.attributes?.tempMarkupEnabled && tempMarkupFeeOptions) && (
              <FeeSection
                title="Contract Markup Percentage"
                feeData={tempMarkupFeeOptions}
                placeholderNote="For all Temp Workers"
              />
            )}
          </div>
        )}
      />
    </div>
  );
}

export default AgencyFeesPopover;
